import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { GalleryImage, ValueCard } from '../types';

// Import aboutus images
import aboutus1 from '../assets/aboutus/aboutus1.webp';
import aboutus2 from '../assets/aboutus/aboutus2.webp';
import aboutus3 from '../assets/aboutus/aboutus3.webp';
import aboutus4 from '../assets/aboutus/aboutus4.webp';
import aboutus5 from '../assets/aboutus/aboutus5.webp';
import aboutus6 from '../assets/aboutus/aboutus6.webp';
import aboutus7 from '../assets/aboutus/aboutus7.webp';

export default function AboutSection() {
  const { t } = useTranslation('about');
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const galleryImages: GalleryImage[] = [
    {
      id: 1,
      imageUrl: aboutus1,
      thumbnailUrl: aboutus1,
      altText: t('gallery.images.0.alt'),
    },
    {
      id: 2,
      imageUrl: aboutus2,
      thumbnailUrl: aboutus2,
      altText: t('gallery.images.1.alt'),
    },
    {
      id: 3,
      imageUrl: aboutus3,
      thumbnailUrl: aboutus3,
      altText: t('gallery.images.2.alt'),
    },
    {
      id: 4,
      imageUrl: aboutus4,
      thumbnailUrl: aboutus4,
      altText: t('gallery.images.3.alt'),
    },
    {
      id: 5,
      imageUrl: aboutus5,
      thumbnailUrl: aboutus5,
      altText: t('gallery.images.4.alt'),
    },
    {
      id: 6,
      imageUrl: aboutus6,
      thumbnailUrl: aboutus6,
      altText: t('gallery.images.5.alt'),
    },
    {
      id: 7,
      imageUrl: aboutus7,
      thumbnailUrl: aboutus7,
      altText: t('gallery.images.6.alt'),
    },
  ];

  const valueCards: ValueCard[] = [
    {
      id: 'skills',
      title: t('values.skills.title'),
      description: t('values.skills.description'),
      icon: (
        <svg
          className='w-5 h-5 text-gray-900'
          fill='currentColor'
          viewBox='0 0 20 20'
        >
          <path d='M11 3a1 1 0 10-2 0v1a1 1 0 102 0V3zM15.657 5.757a1 1 0 00-1.414-1.414l-.707.707a1 1 0 001.414 1.414l.707-.707zM18 10a1 1 0 01-1 1h-1a1 1 0 110-2h1a1 1 0 011 1zM5.05 6.464A1 1 0 106.464 5.05l-.707-.707a1 1 0 00-1.414 1.414l.707.707zM5 10a1 1 0 01-1 1H3a1 1 0 110-2h1a1 1 0 011 1zM8 16v-1h4v1a2 2 0 11-4 0zM12 14c.015-.34.208-.646.477-.859a4 4 0 10-4.954 0c.27.213.462.519.476.859h4.002z' />
        </svg>
      ),
    },
    {
      id: 'character',
      title: t('values.character.title'),
      description: t('values.character.description'),
      icon: (
        <svg
          className='w-5 h-5 text-gray-900'
          fill='currentColor'
          viewBox='0 0 20 20'
        >
          <path d='M2 11a1 1 0 011-1h2a1 1 0 011 1v5a1 1 0 01-1 1H3a1 1 0 01-1-1v-5zM8 7a1 1 0 011-1h2a1 1 0 011 1v9a1 1 0 01-1 1H9a1 1 0 01-1-1V7zM14 4a1 1 0 011-1h2a1 1 0 011 1v12a1 1 0 01-1 1h-2a1 1 0 01-1-1V4z' />
        </svg>
      ),
    },
    {
      id: 'teamwork',
      title: t('values.teamwork.title'),
      description: t('values.teamwork.description'),
      icon: (
        <svg
          className='w-5 h-5 text-gray-900'
          fill='currentColor'
          viewBox='0 0 20 20'
        >
          <path d='M9 6a3 3 0 11-6 0 3 3 0 016 0zM17 6a3 3 0 11-6 0 3 3 0 016 0zM12.93 17c.046-.327.07-.66.07-1a6.97 6.97 0 00-1.5-4.33A5 5 0 0119 16v1h-6.07zM6 11a5 5 0 015 5v1H1v-1a5 5 0 015-5z' />
        </svg>
      ),
    },
    {
      id: 'excellence',
      title: t('values.excellence.title'),
      description: t('values.excellence.description'),
      icon: (
        <svg
          className='w-5 h-5 text-gray-900'
          fill='currentColor'
          viewBox='0 0 20 20'
        >
          <path
            fillRule='evenodd'
            d='M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z'
            clipRule='evenodd'
          />
        </svg>
      ),
    },
  ];

  const nextImage = () => {
    setCurrentImageIndex((prev) => (prev + 1) % galleryImages.length);
  };

  const prevImage = () => {
    setCurrentImageIndex(
      (prev) => (prev - 1 + galleryImages.length) % galleryImages.length
    );
  };

  const goToImage = (index: number) => {
    setCurrentImageIndex(index);
  };

  return (
    <section id='about' className='py-32 bg-white'>
      <div className='max-w-7xl mx-auto px-6'>
        <div className='grid grid-cols-1 lg:grid-cols-2 gap-12 items-start mb-20'>
          {/* Left Column: Text Content */}
          <div>
            <div className='flex items-start mb-8'>
              <div className='gold-bar-vertical'></div>
              <h2 className='text-4xl md:text-6xl font-bold text-gray-900 leading-tight'>
                {t('title')}
              </h2>
            </div>

            <div className='space-y-6 text-lg text-gray-700 leading-relaxed mb-10'>
              <p>
                {t('description.paragraph1')}
              </p>
              <p>
                {t('description.paragraph2')}
              </p>
            </div>

            {/* Key Values Grid */}
            <div className='grid grid-cols-1 sm:grid-cols-2 gap-4'>
              {valueCards.map((card) => (
                <div
                  key={card.id}
                  className='bg-gray-50 border-l-4 border-yellow-400 rounded p-6 hover:shadow-md transition-all'
                >
                  <div className='flex items-center gap-3 mb-2'>
                    <div className='w-10 h-10 hexagon bg-yellow-400 flex items-center justify-center flex-shrink-0'>
                      {card.icon}
                    </div>
                    <h3 className='text-lg font-bold text-gray-900'>
                      {card.title}
                    </h3>
                  </div>
                  <p className='text-sm text-gray-600 pl-13'>
                    {card.description}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* Right Column: Image Gallery */}
          <div className='lg:sticky lg:top-24'>
            <div className='about-gallery relative'>
              {/* Main Image Display */}
              <div className='border-4 border-yellow-400 rounded-lg overflow-hidden mb-4'>
                <div className='gallery-main-container relative'>
                  {/* Gallery Images */}
                  {galleryImages.map((image, index) => (
                    <div
                      key={image.id}
                      className={`gallery-image ${
                        index === currentImageIndex ? 'active' : ''
                      }`}
                      data-index={index}
                    >
                      <img
                        src={image.imageUrl}
                        alt={image.altText}
                        className='w-full h-full object-cover aspect-video'
                      />
                    </div>
                  ))}

                  {/* Gallery Navigation Buttons */}
                  <button
                    onClick={prevImage}
                    className='gallery-nav prev absolute left-2 top-1/2 -translate-y-1/2 w-10 h-10 hexagon bg-yellow-400 bg-opacity-90 hover:bg-opacity-100 flex items-center justify-center transition-all z-10'
                    aria-label={t('aria.previousImage', { ns: 'common' })}
                  >
                    <svg
                      className='w-5 h-5 text-gray-900'
                      fill='none'
                      stroke='currentColor'
                      viewBox='0 0 24 24'
                    >
                      <path
                        strokeLinecap='round'
                        strokeLinejoin='round'
                        strokeWidth='3'
                        d='M15 19l-7-7 7-7'
                      />
                    </svg>
                  </button>
                  <button
                    onClick={nextImage}
                    className='gallery-nav next absolute right-2 top-1/2 -translate-y-1/2 w-10 h-10 hexagon bg-yellow-400 bg-opacity-90 hover:bg-opacity-100 flex items-center justify-center transition-all z-10'
                    aria-label={t('aria.nextImage', { ns: 'common' })}
                  >
                    <svg
                      className='w-5 h-5 text-gray-900'
                      fill='none'
                      stroke='currentColor'
                      viewBox='0 0 24 24'
                    >
                      <path
                        strokeLinecap='round'
                        strokeLinejoin='round'
                        strokeWidth='3'
                        d='M9 5l7 7-7 7'
                      />
                    </svg>
                  </button>

                  {/* Image Counter */}
                  <div className='absolute bottom-4 right-4 bg-gray-900 bg-opacity-75 text-white px-3 py-1 rounded text-sm font-medium z-10'>
                    <span className='gallery-counter'>
                      {currentImageIndex + 1}
                    </span>{' '}
                    / {galleryImages.length}
                  </div>
                </div>
              </div>

              {/* Thumbnail Navigation */}
              <div className='grid grid-cols-6 gap-2'>
                {galleryImages.map((image, index) => (
                  <button
                    key={image.id}
                    onClick={() => goToImage(index)}
                    className={`gallery-thumb border-2 rounded overflow-hidden transition-all hover:opacity-75 ${
                      index === currentImageIndex
                        ? 'active border-yellow-400'
                        : 'border-gray-300 hover:border-yellow-300'
                    }`}
                    data-index={index}
                  >
                    <img
                      src={image.thumbnailUrl}
                      alt={t('aria.thumbnail', { number: index + 1, ns: 'common' })}
                      className='w-full h-full object-cover aspect-video'
                    />
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
